/**
 * Web Component Theme Types
 *
 * IMPORTANT: The actual default values come from @baluu/ui globals.css
 * which defines CSS variables on :root. Webcomponents import globals.css
 * and get those defaults automatically.
 *
 * This file only defines the TYPES and the list of customizable keys.
 * The API only stores OVERRIDES - values a school has explicitly set.
 *
 * Color values can be in any valid CSS format:
 * - hex: #14b8a6
 * - oklch: oklch(0.7787 0.1351 188.7087)
 * - hsl: hsl(174 84% 40%)
 * - rgb: rgb(20 184 166)
 * - named: teal
 */

/**
 * Theme override object - partial, only contains values the school has customized
 * All values are CSS color strings in any valid format
 */
export type WebComponentThemeOverrides = {
  // Core colors
  primary?: string;
  primaryForeground?: string;
  secondary?: string;
  secondaryForeground?: string;
  background?: string;
  foreground?: string;

  // Surface colors
  card?: string;
  cardForeground?: string;
  muted?: string;
  mutedForeground?: string;

  // Accent colors
  accent?: string;
  accentForeground?: string;

  // Feedback colors
  destructive?: string;
  destructiveForeground?: string;

  // Border and input colors
  border?: string;
  input?: string;
  ring?: string;

  // Font overrides - each has URL and name
  // URL can be a Cloudinary upload URL or Google Fonts CSS URL
  fontSansUrl?: string;
  fontSansName?: string;
  fontSerifUrl?: string;
  fontSerifName?: string;
  fontMonoUrl?: string;
  fontMonoName?: string;

  // Raw custom CSS block, injected into the widget's shadow root as the last
  // <style> so it overrides base styles and theme variables. NOT a CSS variable
  // — deliberately excluded from THEME_KEYS so it is never mapped to a --var.
  customCss?: string;
};

/**
 * All customizable theme keys
 * These map to CSS variables: --primary, --primary-foreground, etc.
 */
export const THEME_KEYS = [
  "primary",
  "primaryForeground",
  "secondary",
  "secondaryForeground",
  "background",
  "foreground",
  "card",
  "cardForeground",
  "muted",
  "mutedForeground",
  "accent",
  "accentForeground",
  "destructive",
  "destructiveForeground",
  "border",
  "input",
  "ring",
  // Font keys
  "fontSansUrl",
  "fontSansName",
  "fontSerifUrl",
  "fontSerifName",
  "fontMonoUrl",
  "fontMonoName",
] as const;

/**
 * Font-specific keys (subset of THEME_KEYS)
 */
export const FONT_KEYS = [
  "fontSansUrl",
  "fontSansName",
  "fontSerifUrl",
  "fontSerifName",
  "fontMonoUrl",
  "fontMonoName",
] as const;

export type FontKey = (typeof FONT_KEYS)[number];

export type WebComponentThemeKey = (typeof THEME_KEYS)[number];

/**
 * Convert camelCase to kebab-case for CSS variable names
 * e.g., primaryForeground -> primary-foreground
 */
export function toKebabCase(str: string): string {
  return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
}
